import React, { useRef, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../images/Mass Logo Reconstructed.png";
import { useLanguage } from "../pages/LanguageContext";

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      onClick={onChange}
      aria-pressed={checked}
      aria-label="Toggle Dark Mode"
      type="button"
      tabIndex={0}
      style={{
        width: 54,
        height: 28,
        background: checked ? "#232733" : "#fff",
        borderRadius: 20,
        border: checked ? "1.5px solid #fff" : "1.5px solid #111",
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        cursor: "pointer",
        padding: 0,
        transition: "background 0.22s, border 0.18s",
        boxShadow: checked ? "0 2px 8px #0002" : "0 1px 3px #d6e1f6",
      }}
    >
      <span
        style={{
          width: 26,
          height: 23,
          borderRadius: "50%",
          background: checked ? "#fff" : "#18181a",
          border: "2px solid #bbb",
          position: "absolute",
          left: checked ? 24 : 4,
          top: 2,
          transition:
            "left 0.22s cubic-bezier(.6,-0.4,.52,1.36), background 0.22s, border 0.18s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: checked ? "0 2px 8px #1114" : "0 1.5px 6px #bbb7",
        }}
      />
    </button>
  );
}

const NAV_TEXT_SCALE = 1.11;
const NAV_TEXT_TRANSLATE_Y = -3;

function Header({ darkMode, setDarkMode }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  const { language, toggleLanguage } = useLanguage();

  const menuCloseTimeout = useRef();

  const handleMouseEnterMenuArea = () => {
    clearTimeout(menuCloseTimeout.current);
    setMenuOpen(true);
  };

  const handleMouseLeaveMenuArea = () => {
    menuCloseTimeout.current = setTimeout(() => {
      setMenuOpen(false);
    }, 220);
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @media (min-width: 1025px) {
        .partial-border {
          position: relative;
          font-weight: 600;
          background: none;
          border: none;
          outline: none;
          padding: 0.5rem 1.1rem;
          border-radius: 0 17px 0 0;
          z-index: 1;
          color: inherit;
          background-clip: padding-box;
          min-width: 120px;
          text-align: left;
        }
        .partial-border:hover,
        .partial-border.active {
          color: #1784e6 !important;
          background: linear-gradient(90deg,rgba(26,75,183,0.11),rgba(0,255,195,0.09));
          font-weight: 700;
        }
      }
      .partial-border .nav-link-text {
        display: inline-block;
        transition:
          transform 0.20s cubic-bezier(.52,.02,.74,1.6),
          color 0.19s;
      }
      .partial-border:hover .nav-link-text,
      .partial-border.active .nav-link-text {
        transform: scale(${NAV_TEXT_SCALE}) translateY(${NAV_TEXT_TRANSLATE_Y}px);
        color: #1784e6 !important;
        text-shadow: 0 3px 18px #1784e64a;
      }
      .fade-slide-in {
        animation: fadeSlideIn 0.32s cubic-bezier(.6,.04,.98,.34);
      }
      @keyframes fadeSlideIn {
        0% { opacity: 0; transform: translateY(-14px) scale(0.99); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      .logo-hover:hover {
        transform: scale(1.11);
        transition: transform 0.22s cubic-bezier(.57,.07,.58,1.04);
        box-shadow: 0 4px 24px rgba(30,75,183,0.09);
        z-index: 10;
      }
      .darkmode-rotate {
        transition: transform 0.35s cubic-bezier(.63,-0.17,.67,1.15);
      }
      .darkmode-rotate:active {
        transform: rotate(-20deg) scale(1.15);
      }
      .menu-backdrop {
        position: fixed;
        inset: 0;
        background:#111;
        z-index: 998;
        animation: fadeBackdrop 0.3s;
      }
      @keyframes fadeBackdrop {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const navLabels = {
    Home: { en: "Home", ar: "الرئيسية" },
    Clients: { en: "Clients & Work", ar: "عملاؤنا وأعمالنا" },
    EnvRole: { en: "Environmental Role", ar: "الدور البيئي" },
    AboutUs: { en: "Our Story", ar: "قصتنا" },
    Contact: { en: "Contact", ar: "اتصل بنا" },
  };
  const sitemapLabels = {
    Solutions: { en: "Services", ar: "الخدمات" },
    Technology: { en: "Technology", ar: "التقنيات" },
    INFO: { en: "Info", ar: "معلومات" },
    HomeAutomation: {
      en: "Smart Home  Automation",
      ar: " تركيب أنظمة المنزل الذكي",
    },
    SmartSecurity: { en: "System Support ", ar: "الدعم الفني والصيانة الذكية" },
    ClimateControl: { en: "Network & Security ", ar: "تحسين الشبكة والأمان" },
    Matter: { en: "Matter", ar: "ماتر" },
    Zigbee: { en: "Zigbee", ar: "زيجبي" },
    WiFi: { en: "Wi-Fi", ar: "واي فاي" },
    AboutUs: { en: "Our Story", ar: "قصتنا" },
    ContactUs: { en: "Contact Us", ar: "اتصل بنا" },
    Careers: { en: "Careers", ar: "الوظائف" },
  };

  const navLinks = [
    { to: "/", label: navLabels.Home[language] },
    { to: "/ClientsAndWork", label: navLabels.Clients[language] },
    { to: "/EnvironmentalRole", label: navLabels.EnvRole[language] },
    { to: "/AboutUsPage", label: navLabels.AboutUs[language] },
    { to: "/contact", label: navLabels.Contact[language] },
  ];

  const sitemapLinks = {
    [sitemapLabels.Solutions[language]]: [
      {
        label: sitemapLabels.HomeAutomation[language],
        to: "/SmartHomeAutomationPage",
      },
      {
        label: sitemapLabels.SmartSecurity[language],
        to: "services/home-security",
      },
      {
        label: sitemapLabels.ClimateControl[language],
        to: "services/industrial-application",
      },
    ],
    [sitemapLabels.Technology[language]]: [
      { label: sitemapLabels.Matter[language], to: "/technology/matter" },
      { label: sitemapLabels.Zigbee[language], to: "/technology/zigbee" },
      { label: sitemapLabels.WiFi[language], to: "/technology/wifi" },
    ],
    [sitemapLabels.INFO[language]]: [
      { label: sitemapLabels.AboutUs[language], to: "/AboutUsPage" },
      { label: sitemapLabels.ContactUs[language], to: "/contact" },
      { label: sitemapLabels.Careers[language], to: "/Careers" },
    ],
  };

  const topRoutes = new Set(navLinks.map((n) => n.to));

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth > 1024;
      setIsDesktop(desktop);
      if (!desktop) setMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      const handleClickOutside = (e) => {
        if (menuRef.current && !menuRef.current.contains(e.target)) {
          setMenuOpen(false);
        }
      };
      if (menuOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      } else {
        document.removeEventListener("mousedown", handleClickOutside);
      }
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [menuOpen, isDesktop]);

  const menuAreaProps = isDesktop
    ? {
        onMouseEnter: handleMouseEnterMenuArea,
        onMouseLeave: handleMouseLeaveMenuArea,
      }
    : {};

  const getNavLinkStyle = (isActive = false) => ({
    ...styles.navLink,
    color: isActive
      ? darkMode
        ? "#fff"
        : "#1a4bb7"
      : darkMode
      ? "#90caf9"
      : "#1a4bb7",
    fontWeight: language === "ar" ? 400 : isActive ? 700 : 600,
    letterSpacing: language === "ar" ? "0" : "0.01em",
    fontFamily:
      language === "ar"
        ? "Cairo, Tahoma, Arial, sans-serif"
        : "Segoe UI, Arial, sans-serif",
  });

  return (
    <>
      {menuOpen && !isDesktop && (
        <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
      )}
      <header
        style={{
          ...styles.header,
          backgroundColor: darkMode ? "#111" : "#fff",
          color: darkMode ? "#f3f3f3" : "#141e3a",
          borderBottom: darkMode ? "1px solid #222" : "1px solid #eee",
          direction: language === "ar" ? "rtl" : "ltr",
          fontFamily:
            language === "ar" ? "Cairo, Tahoma, Arial, sans-serif" : undefined,
        }}
      >
        <div style={{ ...styles.container, flexDirection: "row" }}>
          <div style={styles.menuArea} ref={menuRef} {...menuAreaProps}>
            <button
              style={{
                ...styles.menuToggle,
                color: darkMode ? "#fff" : "#1a4bb7",
                background: menuOpen ? (darkMode ? "#111" : "#fff") : "none",
                borderRadius: menuOpen ? "12px" : "50%",
                transition: "background 0.2s",
                boxShadow: menuOpen ? "0 4px 18px :#111" : "none",
              }}
              onClick={() => {
                if (!isDesktop) setMenuOpen(!menuOpen);
              }}
              aria-label="Toggle Menu"
            >
              ☰
            </button>

            {menuOpen && (
              <div
                className="fade-slide-in"
                style={{
                  ...styles.siteMapPanel,
                  background: darkMode
                    ? "linear-gradient(120deg,rgb(13, 13, 16) 90%,#111 100%)"
                    : "linear-gradient(100deg, #fff 90%, #e9f4ff 100%)",
                  color: darkMode ? "#fff" : "#1a4bb7",
                  border: darkMode ? "1px solid #242a31" : "1px solid #e3eaff",
                  boxShadow: "0 12px 36px rgba(27,46,84,0.18)",
                  direction: language === "ar" ? "rtl" : "ltr",
                  fontFamily:
                    language === "ar"
                      ? "Cairo, Tahoma, Arial, sans-serif"
                      : "Segoe UI, Arial, sans-serif",
                  left: language === "ar" ? "auto" : 0,
                  right: language === "ar" ? 0 : "auto",
                }}
                onMouseEnter={isDesktop ? handleMouseEnterMenuArea : undefined}
                onMouseLeave={isDesktop ? handleMouseLeaveMenuArea : undefined}
              >
                {!isDesktop && (
                  <ul
                    style={{
                      ...styles.mobileNavList,
                      textAlign: language === "ar" ? "right" : "left",
                      fontFamily:
                        language === "ar"
                          ? "Cairo, Tahoma, Arial, sans-serif"
                          : "Segoe UI, Arial, sans-serif",
                    }}
                  >
                    {navLinks.map(({ to, label }) => (
                      <li key={to} style={styles.sitemapItem}>
                        <Link
                          to={to}
                          onClick={() => setMenuOpen(false)}
                          style={{
                            ...styles.sitemapLink,
                            fontSize: "1.00rem",
                            fontWeight: 400,
                            color: darkMode ? "#90caf9" : "#0051ffff",
                            fontFamily:
                              language === "ar"
                                ? "Cairo, Tahoma, Arial, sans-serif"
                                : "Segoe UI, Arial, sans-serif",
                          }}
                        >
                          {label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}

                {Object.entries(sitemapLinks).map(([section, items]) => (
                  <div key={section} style={{ marginBottom: "1rem" }}>
                    <div
                      style={{
                        fontSize: "1.00rem",
                        fontWeight: 400,
                        color: darkMode ? "#fff" : "#000000ff",
                        marginBottom: "0.1rem",
                        fontFamily:
                          language === "ar"
                            ? "Cairo, Tahoma, Arial, sans-serif"
                            : "Segoe UI, Arial, sans-serif",
                        letterSpacing: language === "ar" ? "0" : "0.01em",
                      }}
                    >
                      {section}
                    </div>
                    <ul
                      style={{
                        listStyle: "none",
                        paddingLeft: "0.5rem",
                        marginTop: "0.5rem",
                        fontFamily:
                          language === "ar"
                            ? "Cairo, Tahoma, Arial, sans-serif"
                            : "Segoe UI, Arial, sans-serif",
                      }}
                    >
                      {items
                        .filter(
                          (item) =>
                            typeof item === "string" || !topRoutes.has(item.to)
                        )
                        .map((item, idx) =>
                          typeof item === "string" ? (
                            <li key={idx} style={styles.sitemapItem}>
                              {item}
                            </li>
                          ) : (
                            <li key={idx} style={styles.sitemapItem}>
                              <Link
                                to={item.to}
                                onClick={() => setMenuOpen(false)}
                                style={{
                                  ...styles.sitemapLink,
                                  fontSize: "1.00rem",
                                  fontWeight: 400,
                                  color: darkMode ? "#90caf9" : "#0051ffff",
                                  fontFamily:
                                    language === "ar"
                                      ? "Cairo, Tahoma, Arial, sans-serif"
                                      : "Segoe UI, Arial, sans-serif",
                                }}
                              >
                                {item.label}
                              </Link>
                            </li>
                          )
                        )}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link to="/" style={{ ...styles.logoLink, padding: "0 1rem" }}>
            <img
              src={logo}
              alt="Mass Smart Solution Logo"
              style={styles.logoImage}
              className="logo-hover"
            />
          </Link>

          {isDesktop && (
            <nav style={styles.desktopNav}>
              <ul style={styles.desktopNavList}>
                {navLinks.map(({ to, label }) => (
                  <li key={to} style={styles.navItem}>
                    <Link
                      to={to}
                      className={`partial-border${
                        location.pathname === to ? " active" : ""
                      }`}
                      style={getNavLinkStyle(location.pathname === to)}
                    >
                      <span className="nav-link-text">{label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              marginLeft: language === "ar" ? 0 : "auto",
              marginRight: language === "ar" ? "auto" : 0,
              height: "100%",
            }}
          >
            <ToggleSwitch
              checked={darkMode}
              onChange={() => setDarkMode(!darkMode)}
            />

            <button
              onClick={toggleLanguage}
              style={{
                padding: "0.46rem 1.1rem",
                border: "none",
                background: darkMode ? "#111" : "none",
                cursor: "pointer",
                fontSize: "1.13rem",
                color: "#1a4bb7",
                borderRadius: "50%",
                transition: "background 0.19s",
                outline: "none",
                fontFamily:
                  language === "ar"
                    ? "Cairo, Tahoma, Arial, sans-serif"
                    : undefined,
              }}
              title={
                language === "en"
                  ? "تبديل اللغة إلى العربية"
                  : "Switch language to English"
              }
              aria-label="Switch Language"
            >
              {language === "en" ? "عربي" : "EN"}
            </button>
          </div>
        </div>
      </header>
    </>
  );
}

const styles = {
  header: {
    position: "fixed",
    top: 0,
    width: "100%",
    boxShadow: "0 2px 18px rgba(26,75,183,0.05)",
    zIndex: 1200,
    height: "89px",
    transition: "background 0.25s, color 0.25s, border-bottom 0.25s",
    fontFamily: "Segoe UI, Arial, sans-serif",
    letterSpacing: "0.01em",
  },
  container: {
    width: "100%",
    padding: "0 2.5vw",
    minHeight: "89px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    boxSizing: "border-box",
    position: "relative",
  },
  logoLink: {
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    height: "100%",
    marginRight: "1.4rem",
    zIndex: 3,
  },
  logoImage: {
    height: 48,
    userSelect: "none",
    transition: "transform 0.22s",
    willChange: "transform",
  },
  desktopNav: {
    display: "flex",
    alignItems: "center",
  },
  desktopNavList: {
    listStyle: "none",
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    margin: 0,
    padding: 0,
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    margin: "0 0.05rem",
  },
  navLink: {
    fontWeight: 600,
    fontSize: 19,
    textDecoration: "none",
    padding: "0.5rem 1.1rem",
    border: "none",
    background: "none",
    transition: "color 0.27s",
    display: "flex",
    alignItems: "center",
    height: "42px",
    minWidth: "120px",
    justifyContent: "center",
    position: "relative",
    outline: "none",
  },
  menuArea: {
    position: "relative",
    marginRight: "1.2rem",
    zIndex: 11,
    display: "flex",
    alignItems: "center",
    height: "58px",
  },
  menuToggle: {
    fontSize: "2.3rem",
    border: "none",
    background: "none",
    padding: "0.29rem 0.69rem",
    cursor: "pointer",
    outline: "none",
    boxShadow: "none",
    borderRadius: "50%",
    transition: "background 0.18s, color 0.2s",
  },
  siteMapPanel: {
    position: "absolute",
    top: "120%",
    left: 0,
    width: "260px",
    borderRadius: "12px",
    boxShadow: "0 6px 32px rgba(30,51,82,0.13)",
    padding: "1.2rem 1.3rem 1.1rem 1.1rem",
    marginTop: "0.6rem",
    zIndex: 999,
    fontSize: "1rem",
    animation: "fadeSlideIn 0.29s",
  },
  sitemapItem: {
    fontSize: "1rem",
    margin: "0.34rem 0",
    borderRadius: "6px",
    padding: "0.16rem 0.3rem",
    transition: "background 0.13s",
  },
  sitemapLink: {
    textDecoration: "none",
    fontWeight: 500,
    padding: "0.1rem 0.1rem",
    display: "inline-block",
    borderRadius: "4px",
    transition: "background 0.14s, color 0.18s",
  },
  mobileNavList: {
    listStyle: "none",
    marginBottom: "1rem",
    paddingLeft: "0.5rem",
  },
};

export default Header;
